// Your day: the shape of today's visits on one strip, 08:00 to 17:00 (wider only to hold a visit outside it).
// Each visit is a block at its real start and end carrying the patient's name, coloured by its status glyph; the hours
// already gone are shaded, the free stretches dashed, the now line crosses it with its time above. Hover (the native
// tooltip) gives time, patient, visit, room and status; click opens its row in Today.
// Drawn in SVG with percentage attributes (no viewBox) so no position needs an inline style and the names are not
// stretched; the colours are in the bridge under `/* ready list */`.
// Real data only: the strip comes from the appointment list's start and end times and statuses.
import { StatusGlyph } from '@/shared/ui/components/status-glyph';
import type { GlyphKind } from '@/shared/ui/components/status-glyph-tokens';
import { HomeCard } from './home-card';
import {
  countByGlyph,
  dayWindow,
  fmtMinutes,
  freeStretches,
  hhmm,
  timedRows,
  type ReadyRow,
} from './ready-list-data';

const STAGES: Array<{ kind: GlyphKind; label: string }> = [
  { kind: 'done', label: 'done' },
  { kind: 'full', label: 'in session' },
  { kind: 'half', label: 'checked in' },
  { kind: 'empty', label: 'to come' },
  { kind: 'alert', label: 'no show' },
];


export interface YourDayCardProps {
  rows: readonly ReadyRow[];
  nowMs: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function YourDayCard({ rows, nowMs, selectedId, onSelect }: YourDayCardProps) {
  const { from, to } = dayWindow(rows);
  const span = to - from;
  const now = new Date(nowMs);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const { stretches, total } = freeStretches(rows, nowMin, from);
  const next = stretches[0];
  const counts = countByGlyph(rows);
  const blocks = timedRows(rows);
  const hours = Array.from({ length: Math.ceil(span / 60) }, (_, i) => from + i * 60);
  const nowInside = nowMin >= from && nowMin <= to;
  // Positions are percentages of the strip (SVG attributes), so no inline style is needed and text is not stretched.
  const pct = (min: number) => `${((Math.min(to, Math.max(from, min)) - from) / span) * 100}%`;
  const len = (min: number) => `${(min / span) * 100}%`;

  const legend = (
    <span data-day-legend className="day-legend">
      {STAGES.filter((s) => counts[s.kind] > 0).map((s) => (
        <span key={s.kind} className="day-legend-item">
          <StatusGlyph kind={s.kind} />
          {counts[s.kind]} {s.label}
        </span>
      ))}
      {total > 0 && <span className="day-legend-free">{fmtMinutes(total)} free</span>}
    </span>
  );

  return (
    <HomeCard title="Your day" right={legend} data-testid="your-day">
      {rows.length === 0 ? (
        <p className="home-empty">No visits today.</p>
      ) : (
        <>
          {next && (
            <p data-next-free className="day-note">
              Next free stretch {hhmm(next.from)}–{hhmm(next.to)} ({fmtMinutes(next.to - next.from)})
            </p>
          )}
          {nowInside && (
            <svg className="day-now-band" aria-hidden="true">
              <text
                data-now-label
                className="day-now-label"
                x={pct(nowMin)}
                y="50%"
                textAnchor={nowMin - from < span * 0.04 ? 'start' : to - nowMin < span * 0.04 ? 'end' : 'middle'}
                dominantBaseline="central"
              >
                {hhmm(nowMin)}
              </text>
            </svg>
          )}
          <svg className="day-strip" role="group" aria-label={`Today's visits, ${hhmm(from)} to ${hhmm(to)}`}>
            <rect className="day-track" x="0" y="0" width="100%" height="100%" />
            {nowMin > from && <rect data-day-past className="day-past" x="0" y="0" width={pct(Math.min(nowMin, to))} height="100%" />}
            {hours.slice(1).map((h) => (
              <line key={h} className="day-tick" x1={pct(h)} x2={pct(h)} y1="0" y2="100%" />
            ))}
            {stretches.map((st) => (
              <rect key={st.from} data-free className="day-free" x={pct(st.from)} y="0" width={len(st.to - st.from)} height="100%" />
            ))}
            {blocks.map((r) => {
              const label = [r.timeLabel, r.card.patientName, r.visitType, r.card.room, r.card.status].filter(Boolean).join(' · ');
              const start = Math.max(r.startMin, from);
              return (
                <svg
                  key={r.id}
                  className="day-block-box"
                  data-day-block={r.id}
                  data-glyph={r.glyph}
                  data-selected={selectedId === r.id ? '' : undefined}
                  x={pct(start)}
                  y="0"
                  width={len(Math.max(Math.min(r.endMin, to) - start, 1))}
                  height="100%"
                  role="button"
                  tabIndex={0}
                  aria-label={label}
                  onClick={() => onSelect(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(r.id);
                    }
                  }}
                >
                  <title>{label}</title>
                  <rect className="day-block" x="0" y="0" width="100%" height="100%" />
                  <text className="day-block-name" x="8" y="50%" dominantBaseline="central">
                    {r.card.patientName}
                  </text>
                </svg>
              );
            })}
            {nowInside && <line data-now-line className="day-now" x1={pct(nowMin)} x2={pct(nowMin)} y1="0" y2="100%" />}
          </svg>
          <div className="day-hours" aria-hidden="true">
            {hours.map((h) => (
              <span key={h} className="day-hour">
                {hhmm(h)}
              </span>
            ))}
          </div>
          <span className="sr-only">
            {STAGES.filter((s) => counts[s.kind] > 0).map((s) => `${counts[s.kind]} ${s.label}`).join(', ')}
          </span>
        </>
      )}
    </HomeCard>
  );
}

